import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import toast from 'react-hot-toast';
import { IoClose } from 'react-icons/io5';
import { GoArrowUpRight } from 'react-icons/go';
import { TbChevronDown, TbReload, TbSearch, TbX } from 'react-icons/tb';
import { FiRefreshCw } from 'react-icons/fi';
import { Tooltip } from 'react-tooltip';
import { useDebouncedValue, useDidUpdate } from '@mantine/hooks';
import {
  ActionIcon,
  Button as MantineButton,
  Checkbox,
  Menu,
  ScrollArea,
  TextInput
} from '@mantine/core';
import {
  serviceFundMapping,
  useGetDetailedOutputStatsByPeriodId,
  useGetDetailedTransformationStatsByPeriodId
} from '../../../../services/funds.service';
import { useGetTransformationsListByPeriod } from '../../../../services/workflow.services';
import {
  useAutomateSelectedTransformation,
  useCancelAutomateTransformation
} from '../../../../services/automation.service';
import { usePeriodById } from '../../../../services/period.service';
import { useSignalRListener } from '../../../../hooks/useSignalR';
import { SIGNALR_LISTENERS } from '../../../../config/signalr';
import { extractApiError, getLastRefreshLabel } from '../../../../utils';
import { TRANSFORMATION_TYPES } from '../../../../consts/transformationTypes';
import { DeleteConfirmModal } from '../../../../components/DeleteConfirmModal';
import { PermissionGuard } from '../../../../components/PermissionGuard';
import { WEB_PAGE_STATE_ENUMS } from '../../../../components/ChatWidget/constants';
import agentStore from '../../../../store/agent';
import { ControlButton } from '../../../../components/v2';
import { DX, DX_ROOT_CLASS } from '../../../../theme/v2';
import { DataTable } from '../../../../components/v2/DataTable';
import { SortButton } from '../../components/SortButton';
import {
  Container,
  IssuesEmpty,
  IssuesTable,
  IssuesToolbar,
  HeadCell,
  StatusChip,
  DescriptionStyle,
  ErrorMessageStyle,
  FileNameStyle,
  OpenSheetLink,
  ProgressCellStyle,
  customStyles,
  colors,
  Sheet,
  Backdrop
} from '../ExtractionIssuesV2/styles';
import { RefreshedAt, TabButton, TabRow, WarningName } from './styles';

const TABS = [
  { label: 'Transformation', value: 'transformation' },
  { label: 'Output tables', value: 'output_tables' }
];

/* Columns whose value has to be read whole: the cell wraps and the row grows with it. */
const WRAP = { 'data-wrap': 'true' };

/* Three severities, and the platform names them in words. Error and mandatory fix are things that
   stop a filing; a warning is something to have looked at. */
const ERROR_TONE = {
  Error: DX.tone.attention,
  'Mandatory fix': DX.tone.warning,
  Warning: DX.tone.idle
};

const outputParams = element =>
  `${element.table}_${element.category}_${element.error_type}_${element.field}`;

/**
 * The transformation stage of one period, redesigned.
 *
 * A copy of the legacy `TransformationIssuesModal`, in the drawer the extraction stage already
 * opens: the same two queries, the same scratch/automated split, the same re-apply, cancel and
 * refresh, the same routes into the sheet and the output table. What changes is that it is the same
 * table as its neighbour, so a period reads as one thing across its stages.
 *
 * A period set up by hand is not automated: its rows come from the workbook, the platform never
 * asks for its output-table findings, and there is nothing to re-apply. So it gets the list without
 * the tabs — the door that is nailed shut is not drawn.
 */
export const TransformationV2 = ({
  /* The list is empty because the work has only just been asked for, not because it came back
     clean. Told so, the table shows its own skeleton instead of printing "nothing came out wrong"
     over a stage that has not written a row yet. */
  isStarting = false,
  isOpen,
  onClose,
  periodId,
  period,
  worklowId,
  refetch,
  isLatestPeriod,
  canManageWorkflow = true
}) => {
  const navigate = useNavigate();

  const [selectedTransformation, setSelectedTransformation] = useState([]);
  const [activeTab, setActiveTab] = useState(TABS[0].value);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [hasInProgress, setHasInProgress] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchTerm, 300);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const categoryOptionsRef = useRef([]);

  const isScratchPeriod = period?.template_type === 'sratch';

  const apiParams = useMemo(() => {
    const params = {};
    if (debouncedSearch) params.search_query = debouncedSearch;
    if (selectedCategory) params.category_name = selectedCategory.name;
    if (sortConfig.key && !isScratchPeriod) params.sort_by = sortConfig.direction;
    return params;
  }, [isScratchPeriod, debouncedSearch, selectedCategory, sortConfig]);

  const automateSelectedTransformation = useAutomateSelectedTransformation();
  const cancelAutomateTransformation = useCancelAutomateTransformation();

  const { refetch: refetchPeriod } = usePeriodById({
    periodId,
    queryOptions: {
      enabled: !!periodId && isOpen && !isScratchPeriod,
      onSuccess: data => setRefreshedAt(data?.automation_transformation_last_refreshed_time)
    }
  });

  const {
    data: automationIssues,
    refetch: refetchAutomationIssues,
    isLoading: isLoadingAutomation,
    isError: isAutomationError,
    error: automationError
  } = useGetDetailedTransformationStatsByPeriodId({
    periodId,
    params: apiParams,
    queryParams: {
      enabled: !!periodId && isOpen && !isScratchPeriod,
      onSuccess: data => {
        const isOuterStatusInProgress = data.some(item => item.status === 'In progress');
        const isInnerStatusInProgress = data.some(item => item?.task?.status === 'In progress');

        setHasInProgress(isOuterStatusInProgress || isInnerStatusInProgress);
      }
    }
  });

  const {
    data: scratchIssues,
    refetch: refetchScratchIssues,
    isLoading: isLoadingScratch
  } = useGetTransformationsListByPeriod({
    periodId,
    params: apiParams,
    queryParams: {
      enabled: !!periodId && isOpen && isScratchPeriod
    }
  });

  /* File holds sheets, sheet holds transformations; the table reads a row per transformation. */
  const flatScratchIssues = useMemo(() => {
    if (!scratchIssues || !Array.isArray(scratchIssues)) return [];
    return scratchIssues.flatMap(file =>
      (file?.sheets ?? []).flatMap(sheet =>
        (sheet?.transformations ?? []).map(t => ({
          id: t.id,
          file_name: file?.name ?? '',
          file_id: file?.id,
          sheet_name: sheet?.name ?? '',
          sheet_id: sheet?.id,
          is_structured: sheet?.is_structured,
          category: t.category,
          table: t.table
        }))
      )
    );
  }, [scratchIssues]);

  const issues = isScratchPeriod ? flatScratchIssues : automationIssues;

  /* The automated endpoint sorts; the workbook one does not, so its rows are ordered here. */
  const visibleScratchIssues = useMemo(() => {
    if (!isScratchPeriod) return [];
    if (!sortConfig.key) return flatScratchIssues;
    const isAscending = !sortConfig.direction.startsWith('-');

    const FIELD_MAP = { category_name: 'category' };
    const sortField = FIELD_MAP[sortConfig.key] ?? sortConfig.key;
    return [...flatScratchIssues].sort((a, b) => {
      const valueA = (a[sortField] ?? '').toLowerCase();
      const valueB = (b[sortField] ?? '').toLowerCase();
      return isAscending ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
  }, [isScratchPeriod, flatScratchIssues, sortConfig]);

  const refetchIssues = isScratchPeriod ? refetchScratchIssues : refetchAutomationIssues;
  const isLoading = isScratchPeriod ? isLoadingScratch : isLoadingAutomation;

  const {
    data: outputIssues,
    isLoading: isLoadingOutputTables,
    refetch: refetchOutputIssues
  } = useGetDetailedOutputStatsByPeriodId({
    periodId,
    queryParams: {
      enabled: !!periodId && isOpen && !isScratchPeriod,
      /* SignalR drives the live updates; the minute heartbeat is only for missed messages. */
      refetchInterval: hasInProgress && activeTab === 'output_tables' ? 60000 : false,
      onSuccess: data => {
        const isOuterStatusInProgress = data?.some?.(item => item.status === 'In progress');
        const isInnerStatusInProgress = data?.some?.(item => item?.task?.status === 'In progress');

        setHasInProgress(isOuterStatusInProgress || isInnerStatusInProgress);
      }
    }
  });

  const onChangeTab = value => {
    setActiveTab(value);
    setHasInProgress(false);
    setSortConfig({ key: '', direction: '' });
    setSearchTerm('');
    setSelectedCategory(null);
  };

  const onCancelTransformation = useCallback(() => {
    const taskIds =
      selectedTransformation.map(
        item => issues.find(value => value.previous_transformation_id === item)?.task.id
      ) || [];

    if (taskIds.length === 0)
      return toast.error('No tasks are detected on selected transformations');

    return cancelAutomateTransformation.mutate(
      { task_ids: taskIds },
      {
        onSuccess: () => {
          setSelectedTransformation([]);
          refetch();
          refetchIssues();
          toast.success('Automated transformations have been successfully cancelled', {
            position: 'bottom-center'
          });
        }
      }
    );
  }, [selectedTransformation, issues, refetch, refetchIssues, cancelAutomateTransformation]);

  const onSelectTransformation = id => {
    if (selectedTransformation.includes(id)) {
      setSelectedTransformation(prev => prev.filter(value => value !== id));
    } else {
      setSelectedTransformation(prev => [...prev, id]);
    }
  };

  const isAllSelected = useMemo(() => {
    const selectableIssues = Array.isArray(issues)
      ? issues.filter(item => item?.transformation_type !== TRANSFORMATION_TYPES.MANUAL)
      : [];
    return (
      selectableIssues.length > 0 &&
      selectableIssues.every(item =>
        selectedTransformation.includes(item.previous_transformation_id)
      )
    );
  }, [selectedTransformation, issues]);

  const toggleAllTransformations = () => {
    if (isAllSelected) {
      setSelectedTransformation([]);
    } else {
      setSelectedTransformation(
        Array.isArray(issues)
          ? issues
              ?.filter(item => item?.transformation_type !== TRANSFORMATION_TYPES.MANUAL)
              ?.map(item => item.previous_transformation_id)
          : []
      );
    }
  };

  const onReapply = () => {
    automateSelectedTransformation.mutate(
      {
        period_id: periodId,
        process_pairs: [...selectedTransformation].map(prevTransformationId => ({
          previous_transformation_id: prevTransformationId,
          extraction_mapping_id: issues.find(
            item => item.previous_transformation_id === prevTransformationId
          )?.extraction_mapping_id
        }))
      },
      {
        onSuccess: () => {
          setOpenConfirmModal(false);
          setSelectedTransformation([]);
          refetch();
          refetchIssues();
          toast.success('Automated transformations have been successfully applied', {
            position: 'bottom-center'
          });
        },
        onError: err => {
          toast.error(extractApiError(err, 'Failed to apply automated transformation'), {
            position: 'bottom-center'
          });
        }
      }
    );
  };

  const refreshAutomationTransformation = useCallback(
    async ({ silent = false } = {}) => {
      setIsRefreshing(true);

      try {
        await serviceFundMapping.getRefreshTransformationStats(periodId);
        await refetchIssues();
        if (!silent) toast.success('Transformations have been successfully refreshed');
      } catch (err) {
        refetch();
        refetchPeriod();
        if (!silent) toast.error(extractApiError(err, 'Failed to refresh transformation'));
      } finally {
        setIsRefreshing(false);
      }
    },
    [periodId, refetch, refetchIssues, refetchPeriod]
  );

  const handleTransformationSignal = data => {
    if (
      data?.period_id === periodId &&
      isOpen &&
      !isScratchPeriod &&
      activeTab === 'transformation'
    ) {
      refetchIssues();
    }
  };

  useSignalRListener(
    SIGNALR_LISTENERS.ON_AUTOMATION_TRANSFORMATION_STATUS_UPDATED,
    handleTransformationSignal
  );
  useSignalRListener(SIGNALR_LISTENERS.ON_TRANSFORMATION_UPDATED, handleTransformationSignal);

  useSignalRListener(SIGNALR_LISTENERS.ON_OUTPUT_TABLE_STATUS_UPDATED, data => {
    if (
      data?.period_id === periodId &&
      isOpen &&
      !isScratchPeriod &&
      activeTab === 'output_tables'
    ) {
      refetchOutputIssues();
    }
  });

  /* Into the sheet. The redesigned transformation page reads `extractId` and
     `extractedTransformationTable`, which is what this has always sent; the classic one needs
     `tab=transformation` on top, because there it is one page with three tabs. */
  const onNavigate = (fileId, sheetId, category, extractionId, table) => {
    const extractParam = extractionId != null ? `&extractId=${extractionId}` : '';
    const tableParam = table ? `&extractedTransformationTable=${table}` : '';
    const query = `?fileId=${fileId}&sheetId=${sheetId}&category=${category}${extractParam}${tableParam}`;
    navigate(`/data-extraction/${worklowId}/${periodId}${query}&tab=transformation`);
  };

  /* Into the output table. The two pages want the finding named differently — the classic one takes
     one packed string it splits back apart, the redesigned one takes the table and the category as
     themselves — so this is a translation rather than a swapped prefix. */
  const onNavigateOutputTables = el =>
    navigate(
      /* Both designs live at this address now, and each reads the finding its own way: the classic
         page splits one packed string, the redesigned one takes the table and category as
         themselves. Sending both costs nothing and neither minds the other's. */
      `/output-tables/${worklowId}/${periodId}?initial_params=${outputParams(el)}` +
        `&outputTable=${encodeURIComponent(el.table ?? '')}` +
        `&outputCategory=${encodeURIComponent(el.category ?? '')}`
    );

  const transformationIssuesCount = useMemo(() => {
    if (!issues) return 0;
    if (isScratchPeriod) return issues.length;

    return issues.filter(el => el.errors?.length > 0).length;
  }, [issues, isScratchPeriod]);

  const mutatedOutputIssues = useMemo(() => {
    if (!outputIssues) return [];

    return [
      ...(outputIssues.errors || []),
      ...(outputIssues.mandatory_fixes || []),
      ...(outputIssues.warnings || [])
    ];
  }, [outputIssues]);

  const counts = {
    transformation: transformationIssuesCount,
    output_tables: mutatedOutputIssues.length
  };

  const tabTone = value => {
    if (!counts[value]) return undefined;
    if (value === 'transformation') {
      if (issues?.some(issue => (issue?.task?.status || issue?.status) === 'Failed'))
        return 'attention';
      if (issues?.some(issue => (issue?.task?.status || issue?.status) === 'In progress'))
        return 'warning';
      return undefined;
    }
    if (mutatedOutputIssues.some(issue => issue?.error_type === 'Error')) return 'attention';
    if (mutatedOutputIssues.some(issue => issue?.error_type === 'Mandatory fix')) return 'warning';
    return undefined;
  };

  const categoryOptions = useMemo(() => {
    if (!issues?.length) {
      categoryOptionsRef.current = [];
      return categoryOptionsRef.current;
    }
    if (!selectedCategory) {
      const unique = [...new Set(issues.map(item => item.category).filter(Boolean))];
      categoryOptionsRef.current = unique
        .sort()
        .map(category => ({ id: category, name: category }));
    }
    return categoryOptionsRef.current;
  }, [issues, selectedCategory]);

  const tableData = isScratchPeriod ? visibleScratchIssues : (automationIssues ?? []);

  const isMutating =
    automateSelectedTransformation.isLoading ||
    cancelAutomateTransformation.isLoading ||
    selectedTransformation.length === 0;

  const isSelectedTransformationsInProgress = useMemo(() => {
    return selectedTransformation.some(el => {
      const status = issues?.find(rec => rec.previous_transformation_id === el)?.status;
      return status === 'In progress';
    });
  }, [selectedTransformation, issues]);

  /* Every row says which file it came from; a row that repeats the one above says it quietly. */
  const leadRows = new Set();
  let previousFileName = null;
  tableData.forEach(record => {
    if (record.file_name !== previousFileName) leadRows.add(record);
    previousFileName = record.file_name;
  });

  const columns = [
    {
      key: 'file_name',
      title: 'File name',
      thStyle: { width: 260 },
      tdProps: WRAP,
      renderHeadCell: () => (
        <HeadCell>
          {isLatestPeriod && !isScratchPeriod && (
            <Checkbox
              checked={isAllSelected}
              indeterminate={selectedTransformation.length > 0 && !isAllSelected}
              onChange={toggleAllTransformations}
              aria-label="Select all transformations"
            />
          )}
          File name
          <SortButton
            columnName="file_name"
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        </HeadCell>
      ),
      render: record => {
        const isManually = record?.transformation_type === TRANSFORMATION_TYPES.MANUAL;

        return (
          <FileNameStyle title={record.file_name}>
            {isLatestPeriod && !isScratchPeriod && (
              <Checkbox
                disabled={isManually}
                checked={selectedTransformation.includes(record.previous_transformation_id)}
                onChange={() => onSelectTransformation(record.previous_transformation_id)}
                aria-label={`Select ${record.sheet_name}`}
              />
            )}
            <span data-repeat={!leadRows.has(record)}>{record.file_name}</span>
          </FileNameStyle>
        );
      }
    },
    {
      key: 'sheet_name',
      title: 'Sheet name',
      thStyle: { width: 160 },
      tdProps: WRAP,
      renderHeadCell: () => (
        <HeadCell>
          Sheet name
          <SortButton
            columnName="sheet_name"
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        </HeadCell>
      )
    },
    {
      key: 'table_structure',
      title: 'Table structure',
      /* Wide enough for its own caption: 'Table structure' was the one header the table clipped. */
      thStyle: { width: 140 },
      tdProps: WRAP,
      render: record =>
        record.is_structured == null ? '-' : record.is_structured ? 'Structured' : 'Unstructured'
    },
    {
      key: 'category',
      title: 'Category',
      thStyle: { width: 130 },
      tdProps: WRAP,
      renderHeadCell: () => (
        <HeadCell>
          Category
          <SortButton
            columnName="category_name"
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        </HeadCell>
      )
    },
    {
      key: 'table',
      title: 'Table',
      thStyle: { width: 130 },
      tdProps: WRAP,
      render: record => record.table ?? '-'
    },
    ...(isScratchPeriod
      ? [
          {
            key: 'open',
            title: '',
            thStyle: { width: 110 },
            render: record => (
              <DescriptionStyle>
                <OpenSheetLink
                  onClick={() =>
                    onNavigate(
                      record.file_id,
                      record.sheet_id,
                      record.category,
                      undefined,
                      record.table
                    )
                  }
                >
                  Open sheet
                  <GoArrowUpRight size={13} />
                </OpenSheetLink>
              </DescriptionStyle>
            )
          }
        ]
      : [
          {
            key: 'status',
            title: 'Status',
            thStyle: { width: 140 },
            render: record => {
              const isManually = record?.transformation_type === TRANSFORMATION_TYPES.MANUAL;
              const label = isManually
                ? TRANSFORMATION_TYPES.MANUAL
                : record.status || 'Not transformed';

              return (
                <ProgressCellStyle>
                  <StatusChip $tone={isManually ? colors.Manually : colors[label]}>
                    <i className="mark" aria-hidden />
                    {label}
                  </StatusChip>
                </ProgressCellStyle>
              );
            }
          },
          {
            key: 'issue_description',
            title: 'Issue description',
            tdProps: WRAP,
            /* Every reason, in full. This is the sentence the row exists for: an id that is not
               found is the id you are about to go looking for, and half of one is no use. */
            render: record => {
              const errors = record.errors || [];
              const taskError = record.latest_automation_task?.error_message;
              const messages = errors.length
                ? errors.map(item => item.error_message)
                : taskError
                  ? [taskError]
                  : [];

              return (
                <DescriptionStyle>
                  {messages.length > 0 && (
                    <ErrorMessageStyle data-tone={errors.length > 0 ? 'warning' : undefined}>
                      <span>
                        {messages.map((text, index) => (
                          <span key={`${text}-${index}`} className="line">
                            {messages.length > 1 ? `${index + 1}. ${text}` : text}
                          </span>
                        ))}
                      </span>
                    </ErrorMessageStyle>
                  )}

                  <OpenSheetLink
                    onClick={() =>
                      onNavigate(
                        record.file_id,
                        record.sheet_id,
                        record.category,
                        errors.length > 0 ? undefined : record.extraction_id,
                        /* The table the row names. A failed row has no extraction to point at, and
                           without either the page opens on nothing — which is what the sheet-and-
                           file pair used to prevent on the classic page and cannot here: the
                           redesigned one finds its way by table, not by sheet. */
                        record.table
                      )
                    }
                  >
                    Open sheet
                    <GoArrowUpRight size={13} />
                  </OpenSheetLink>
                </DescriptionStyle>
              );
            }
          }
        ])
  ];

  const outputTableColumns = [
    {
      key: 'name',
      title: 'Finding',
      thStyle: { width: 280 },
      tdProps: WRAP,
      render: record => (
        <WarningName
          $tone={ERROR_TONE[record.error_type]}
          onClick={() => onNavigateOutputTables(record)}
          title={record.field || 'N/A'}
        >
          <i className="mark" aria-hidden />
          <span className="label">{record.field || 'N/A'}</span>
          {record?.rows?.length > 0 && <span className="n">{record.rows.length}</span>}
        </WarningName>
      )
    },
    {
      key: 'error_type',
      title: 'Kind',
      thStyle: { width: 130 },
      render: record => record.error_type || '-'
    },
    {
      key: 'message',
      title: 'What it says',
      tdProps: WRAP,
      render: record => (
        <DescriptionStyle>
          <ErrorMessageStyle>
            <span>{record.message}</span>
          </ErrorMessageStyle>
          <OpenSheetLink onClick={() => onNavigateOutputTables(record)}>
            Open table
            <GoArrowUpRight size={13} />
          </OpenSheetLink>
        </DescriptionStyle>
      )
    }
  ];

  useDidUpdate(() => {
    if (isOpen) {
      setActiveTab(TABS[0].value);
      agentStore.setCurrentSubScreen(WEB_PAGE_STATE_ENUMS.AutomationTransformationDashboard);
    } else {
      agentStore.setCurrentSubScreen('');
      setSelectedTransformation([]);
      setSortConfig({ key: '', direction: '' });
      setSearchTerm('');
      setSelectedCategory(null);
    }
  }, [isOpen]);

  const onTransformationTab = activeTab === 'transformation' || isScratchPeriod;
  const rowCount = onTransformationTab ? tableData.length : mutatedOutputIssues.length;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        style={customStyles}
        /* Both are styled elements of our own so react-modal hangs its classes on them rather than on
         the global ones every legacy modal shares. */
        contentElement={(props, children) => <Sheet {...props}>{children}</Sheet>}
        overlayElement={(props, contentEl) => <Backdrop {...props}>{contentEl}</Backdrop>}
      >
        <Container className={DX_ROOT_CLASS}>
          <IssuesToolbar>
            <h3>{isScratchPeriod ? 'Transformation' : 'Transformation issues'}</h3>

            {/* A period set up by hand has no output-table findings to fetch, so it has one view. */}
            {!isScratchPeriod && (
              <TabRow>
                {TABS.map(tab => (
                  <TabButton
                    key={tab.value}
                    data-active={tab.value === activeTab}
                    data-tone={tabTone(tab.value)}
                    onClick={() => onChangeTab(tab.value)}
                  >
                    {tab.label}
                    <span className="n">{counts[tab.value]}</span>
                  </TabButton>
                ))}
              </TabRow>
            )}

            <span className="spacer" />

            {onTransformationTab && (
              <>
                <TextInput
                  placeholder="Search sheets"
                  leftSection={<TbSearch size={15} color="var(--dx-text-faint)" />}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  size="xs"
                  w={248}
                />

                <Menu shadow="sm" width={220} zIndex={1100}>
                  <Menu.Target>
                    <ControlButton data-active={!!selectedCategory}>
                      {selectedCategory ? selectedCategory.name : 'Category'}
                      <TbChevronDown size={14} />
                    </ControlButton>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <ScrollArea.Autosize mah={300}>
                      <Menu.Item fz="13px" onClick={() => setSelectedCategory(null)}>
                        All categories
                      </Menu.Item>
                      {categoryOptions.map(option => (
                        <Menu.Item
                          key={option.id}
                          fz="13px"
                          onClick={() => setSelectedCategory(option)}
                        >
                          {option.name}
                        </Menu.Item>
                      ))}
                    </ScrollArea.Autosize>
                  </Menu.Dropdown>
                </Menu>
              </>
            )}

            <span className="count">
              {selectedTransformation.length > 0
                ? `${selectedTransformation.length} selected`
                : `${rowCount} ${onTransformationTab ? 'sheet' : 'finding'}${rowCount === 1 ? '' : 's'}`}
            </span>

            {!isScratchPeriod && activeTab === 'transformation' && (
              <>
                {refreshedAt && <RefreshedAt>{getLastRefreshLabel(refreshedAt)}</RefreshedAt>}

                <MantineButton
                  size="xs"
                  variant="default"
                  leftSection={<FiRefreshCw fontSize="14px" />}
                  onClick={refreshAutomationTransformation}
                  disabled={isRefreshing || issues?.length === 0}
                >
                  Refresh
                </MantineButton>
              </>
            )}

            {isLatestPeriod &&
              !isScratchPeriod &&
              activeTab === 'transformation' &&
              selectedTransformation.length > 0 &&
              isSelectedTransformationsInProgress && (
                <MantineButton
                  size="xs"
                  variant="default"
                  leftSection={<IoClose fontSize="15px" />}
                  disabled={isMutating}
                  onClick={onCancelTransformation}
                >
                  Cancel transformation
                </MantineButton>
              )}

            {isLatestPeriod && !isScratchPeriod && activeTab === 'transformation' && (
              <PermissionGuard allowed={canManageWorkflow}>
                <MantineButton
                  size="xs"
                  leftSection={<TbReload fontSize="15px" />}
                  onClick={() => setOpenConfirmModal(true)}
                  disabled={isMutating}
                >
                  Re-apply
                </MantineButton>
              </PermissionGuard>
            )}

            <ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label="Close">
              <TbX size={16} />
            </ActionIcon>
          </IssuesToolbar>

          <IssuesTable>
            {onTransformationTab ? (
              <DataTable
                idPrefix="transformation-issues-table"
                columns={columns}
                data={tableData}
                isLoading={isLoading || (isStarting && !tableData.length)}
                /* Three different silences: a refusal, an empty list, and a clean run. Only the
                   last one is good news, so only the last one says so. */
                empty={
                  <IssuesEmpty>
                    {isAutomationError
                      ? `The transformation statistics could not be read. ${automationError?.message || ''}`.trim()
                      : isScratchPeriod
                        ? 'No transformations have been set up for this period.'
                        : 'Nothing came out wrong here.'}
                  </IssuesEmpty>
                }
              />
            ) : (
              <DataTable
                idPrefix="output-tables-issues-table"
                columns={outputTableColumns}
                data={mutatedOutputIssues}
                isLoading={isLoadingOutputTables}
                empty={<IssuesEmpty>The output tables have nothing to flag.</IssuesEmpty>}
              />
            )}
          </IssuesTable>

          <Tooltip
            id="warning-tooltip"
            data-testid="warning-tooltip"
            style={{ background: '#868E96', padding: '8px' }}
          />
        </Container>
      </Modal>

      <DeleteConfirmModal
        isOpen={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        title="Re-apply Transformation"
        description="The transformation actions from previous period will be applied for the selected items and overwrites the existing actions. Do you want to re-apply the transformations from previous period?"
        cancelLabel="No"
        submitLabel="Yes"
        onConfirm={onReapply}
        submitColor="blue.6"
        isLoading={automateSelectedTransformation.isLoading}
        /* The drawer under it is a react-modal at 1000/1001; Mantine's own 200 would put this
           behind it. */
        zIndex={2000}
      />
    </>
  );
};
